import { useState } from 'react'
import { Printer, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { api } from '@/services'
import type { Room, Building } from '@/types'

interface RequestPrintDialogProps {
  building: Building
  rooms: Room[]
}

/**
 * Admin-only dialog for requesting professional QR code prints from Tidy Tag.
 * Sends building/org/room details to the Tidy Tag fulfillment inbox.
 */
export function RequestPrintDialog({
  building,
  rooms,
}: RequestPrintDialogProps) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)

    try {
      await api.post('/print-requests', {
        building_id: building.id,
        building_name: building.name,
        building_address: building.address ?? '',
        room_count: rooms.length,
        rooms: rooms.map((r) => ({
          id: r.id,
          name: r.name,
          floor: r.floor ?? '',
        })),
      })
      setSubmitted(true)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to submit print request. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) {
      // Reset state when dialog closes
      setSubmitted(false)
      setError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Mail className="mr-2 h-4 w-4" />
          Request Print from Tidy Tag
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Professional QR Code Prints</DialogTitle>
          <DialogDescription>
            Order professionally printed, durable QR code labels for all rooms
            in {building.name}.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
              Your print request has been submitted. The Tidy Tag team will
              reach out to confirm your order and arrange delivery.
            </div>
            <DialogFooter>
              <Button onClick={() => handleOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-muted/50 p-4 text-sm space-y-3">
              <p>
                Tidy Tag offers a professional print service for durable,
                weather-resistant QR code labels. These labels are designed to
                withstand cleaning environments and last longer than standard
                paper prints.
              </p>
              <p className="text-muted-foreground">
                This is a paid service. The Tidy Tag team will contact you with
                pricing and delivery details after you submit your request.
              </p>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Building</span>
                <span className="font-medium">{building.name}</span>
              </div>
              {building.address && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Address</span>
                  <span className="font-medium">{building.address}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Number of QR codes</span>
                <span className="font-medium">{rooms.length}</span>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || rooms.length === 0}
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Printer className="mr-2 h-4 w-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
